'use client'

import { LATEST_TOS_VERSION, needsToAcceptNewTerms } from "@/lib/auth/config/tos-config"
import { SetupStep } from "@/lib/types/auth/verification"
import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { safeProperty } from '@/lib/utils/type-safety'
import { TermsOfServiceModal } from "./tos-modal"

interface TosCheckProps {
  children: React.ReactNode
}

export function TosCheck({ children }: TosCheckProps) {
  const { data: session, status } = useSession()
  const router = useRouter()
  const [showTos, setShowTos] = useState(false)
  const [currentTosVersion, setCurrentTosVersion] = useState(LATEST_TOS_VERSION)

  // Check if user needs to accept ToS
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      const tosAccepted = safeProperty(session.user, 'tosAccepted' as any, false)
      const setupStepsRaw = safeProperty(session.user, 'setupSteps' as any, [])
      const setupSteps = Array.isArray(setupStepsRaw) ? setupStepsRaw : []

      // Check for both uppercase and lowercase versions of the terms_of_service step
      const hasCompletedTosStep = setupSteps.some((step: string) =>
        step === SetupStep.TERMS_OF_SERVICE ||
        step === 'terms_of_service' ||
        step === 'TERMS_OF_SERVICE'
      )

      const userTosVersion = safeProperty(session.user, 'tosVersion' as any, null)

      // Only show ToS modal for users who truly haven't accepted it
      // Don't show for existing users who have already accepted
      const needsToAccept = !tosAccepted && !hasCompletedTosStep
      const needsNewVersion = tosAccepted && needsToAcceptNewTerms(userTosVersion)

      if (needsToAccept || needsNewVersion) {
        setShowTos(true)
      } else {
        setShowTos(false)
      }
    }
  }, [session, status])

  // Don't render anything special if not authenticated or ToS doesn't need to be shown
  if (status !== 'authenticated' || !showTos) {
    return <>{children}</>
  }

  // Get the user ID
  const userId = session?.user?.id

  if (!userId) {
    // If no user ID, just render children
    return <>{children}</>
  }

  return (
    <>
      {children}

      <TermsOfServiceModal
        isOpen={showTos}
        onClose={() => {}} // Don't allow closing
        userId={userId}
        version={currentTosVersion}
        onAccepted={() => setShowTos(false)}
      />
    </>
  )
}
